import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@client/components/hooks/use-toast';
import {
  selectSessionAdvisor,
  selectPendingSeatInvitations,
  selectSeatInvitationAcceptResponse,
  selectSeatInvitationDeclineResponse,
  selectSession,
} from '@client/model/selectors';
import {
  postSeatInvitationAcceptBySubscriptionSeatIdRequest,
  postSeatInvitationDeclineBySubscriptionSeatIdRequest,
  fetchSubscriptionSeatsByAdvisorIdRequest,
} from '@client/model/subscriptionSeatsModel';
import { SessionType } from '@shared/SessionType';

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

/**
 * Hook for managing accept/decline actions on pending seat invitations
 * from the Subscription page.
 *
 * This hook handles:
 * - Fetching pending invitations from the Redux store
 * - Managing accept/decline dialog state
 * - Dispatching accept/decline requests with consent data
 * - Showing success/error toast notifications
 * - Auto-refetching seats on success
 */
export function useSeatInvitationAcceptDecline() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);
  const pendingSeatInvitations = useSelector(selectPendingSeatInvitations);
  const acceptResponse = useSelector(selectSeatInvitationAcceptResponse);
  const declineResponse = useSelector(selectSeatInvitationDeclineResponse);

  // Dialog state for accept (with consent)
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [invitationToAccept, setInvitationToAccept] = useState<PendingSeatInvitation | null>(null);

  // Dialog state for decline confirmation
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [invitationToDecline, setInvitationToDecline] = useState<PendingSeatInvitation | null>(
    null,
  );

  // Track previous mutation states for completion detection
  const prevAcceptingRef = useRef(false);
  const prevDecliningRef = useRef(false);

  const isAccepting = acceptResponse.status === 'loading';
  const isDeclining = declineResponse.status === 'loading';
  const isProcessing = isAccepting || isDeclining;

  /**
   * Handles post-mutation navigation based on session type.
   * New advisors in signup flow are redirected to action required view.
   * For existing advisors, we refetch the subscription seats.
   */
  const handlePostMutationAction = useCallback(() => {
    if (
      sessionType === SessionType.advisorUnverifiedAccount ||
      sessionType === SessionType.advisorNoSubscription
    ) {
      if (sessionAdvisor?.advisorId) {
        navigate(`/advisors/${sessionAdvisor.advisorId}/account-action-required`);
      }
    } else if (sessionAdvisor?.advisorId) {
      // For other session types, refetch the subscription seats
      dispatch(fetchSubscriptionSeatsByAdvisorIdRequest(sessionAdvisor.advisorId));
    }
  }, [sessionType, sessionAdvisor?.advisorId, navigate, dispatch]);

  // Handle accept mutation completion
  useEffect(() => {
    if (prevAcceptingRef.current && !isAccepting) {
      if (acceptResponse.status === 'error') {
        toast({
          title: 'Error',
          description: acceptResponse.error || 'Failed to accept invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (acceptResponse.status === 'success') {
        toast({
          title: 'Invitation Accepted',
          description: acceptResponse.message || 'Your subscription seat has been activated.',
          className: '-translate-y-5',
        });

        setAcceptDialogOpen(false);
        setInvitationToAccept(null);
        handlePostMutationAction();
      }
    }

    prevAcceptingRef.current = isAccepting;
  }, [
    isAccepting,
    acceptResponse.status,
    acceptResponse.error,
    acceptResponse.message,
    handlePostMutationAction,
    toast,
  ]);

  // Handle decline mutation completion
  useEffect(() => {
    if (prevDecliningRef.current && !isDeclining) {
      if (declineResponse.status === 'error') {
        toast({
          title: 'Error',
          description: declineResponse.error || 'Failed to decline invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (declineResponse.status === 'success') {
        toast({
          title: 'Invitation Declined',
          description: declineResponse.message || 'The invitation has been declined.',
          className: '-translate-y-5',
        });

        setDeclineDialogOpen(false);
        setInvitationToDecline(null);
        handlePostMutationAction();
      }
    }

    prevDecliningRef.current = isDeclining;
  }, [
    isDeclining,
    declineResponse.status,
    declineResponse.error,
    declineResponse.message,
    handlePostMutationAction,
    toast,
  ]);

  /**
   * Opens the accept dialog for a specific invitation
   */
  const handleAccept = useCallback((invitation: PendingSeatInvitation) => {
    setInvitationToAccept(invitation);
    setAcceptDialogOpen(true);
  }, []);

  /**
   * Confirms acceptance with consent data
   */
  const confirmAccept = useCallback(
    (consentData: ConsentData) => {
      if (!invitationToAccept) return;

      dispatch(
        postSeatInvitationAcceptBySubscriptionSeatIdRequest({
          subscriptionSeatId: invitationToAccept.subscriptionSeatId,
          ipAddress: consentData.ipAddress,
          userAgent: consentData.userAgent,
          metadata: { consentCapturedAt: new Date().toISOString() },
        }),
      );
    },
    [dispatch, invitationToAccept],
  );

  /**
   * Cancels the accept dialog
   */
  const cancelAccept = useCallback(() => {
    setAcceptDialogOpen(false);
    setInvitationToAccept(null);
  }, []);

  /**
   * Opens the decline confirmation dialog for a specific invitation
   */
  const handleDecline = useCallback((invitation: PendingSeatInvitation) => {
    setInvitationToDecline(invitation);
    setDeclineDialogOpen(true);
  }, []);

  /**
   * Confirms decline - keeps dialog open while processing
   */
  const confirmDecline = useCallback(() => {
    if (!invitationToDecline) return;

    dispatch(
      postSeatInvitationDeclineBySubscriptionSeatIdRequest({
        subscriptionSeatId: invitationToDecline.subscriptionSeatId,
      }),
    );
    // Dialog will be closed by the useEffect when decline completes
  }, [dispatch, invitationToDecline]);

  /**
   * Cancels the decline dialog
   */
  const cancelDecline = useCallback(() => {
    setDeclineDialogOpen(false);
    setInvitationToDecline(null);
  }, []);

  return {
    // Data
    pendingSeatInvitations,

    // Accept dialog state
    acceptDialogOpen,
    invitationToAccept,

    // Decline dialog state
    declineDialogOpen,
    invitationToDecline,

    // Mutation state
    isAccepting,
    isDeclining,
    isProcessing,

    // Actions
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  };
}

export type UseSeatInvitationAcceptDeclineReturn = ReturnType<
  typeof useSeatInvitationAcceptDecline
>;
